import React from 'react';
import { Box, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { useTemplateContext } from '@components/Contexts/TemplateContext';
import { useIsDirectoryPageEnabled } from '@utils/useIsDirectoryPageEnabled';
import { buildPartnersProfileUrl } from '@utils/partnersDirectoryPaths';
import { ENGLISH_LANGUAGE_CODE } from '@utils/constants';
import { useMoreByPartnerTemplates } from '../useRecommendedTemplates';
import RecommendedSection from '../RecommendedSection';

function getPartnerTemplatesUrl(
  slug: string,
  language = 'en',
  isCommunitySubdomainEnabled: boolean,
) {
  const subdomain = language === ENGLISH_LANGUAGE_CODE ? 'www' : language;
  return `${buildPartnersProfileUrl({
    host: `https://${subdomain}.wix.com`,
    slug,
    pathLanguage: language !== ENGLISH_LANGUAGE_CODE ? language : undefined,
    useCommunitySubdomain: isCommunitySubdomainEnabled,
  })}#templates`;
}

const SHOW_MORE_FOR_MORE_THEN_SITES_NUMBER = 4;

interface Props {
  isMobileView?: boolean;
}

const MoreByPartner: React.FC<Props> = ({ isMobileView = false }) => {
  const { t, i18n } = useTranslation();
  const isCommunitySubdomainEnabled = useIsDirectoryPageEnabled();
  const { template } = useTemplateContext();
  const partnerName = template.partnerProfileDetails?.businessName;
  const slug = template.partnerProfileDetails?.slug;
  const { data, isLoading } = useMoreByPartnerTemplates(template);
  const partnerTemplates = data?.templates;
  const totalCount = data?.totalCount ?? 0;

  if (isLoading || !partnerTemplates || partnerTemplates.length === 0) {
    return null;
  }

  return (
    <RecommendedSection
      title={
        <>
          {t('marketplace.template-page.more-by-partner.prefix', {
            partnerName,
          })}
        </>
      }
      action={
        slug && totalCount > SHOW_MORE_FOR_MORE_THEN_SITES_NUMBER ? (
          <Box margin={isMobileView ? '0 24px 0 30px' : '0'}>
            <TextButton
              target="_blank"
              skin="dark"
              underline="always"
              as="a"
              href={getPartnerTemplatesUrl(
                slug,
                i18n.language,
                isCommunitySubdomainEnabled,
              )}
            >
              {t('marketplace.template-page.more-by-partner.see-all')}
            </TextButton>
          </Box>
        ) : undefined
      }
      templates={partnerTemplates}
      sectionType="more_by_partner"
      dataHook="more-by-partner"
      isMobileView={isMobileView}
    />
  );
};

export default MoreByPartner;
